<template>
  <article>
    <time v-if="date" :datetime="date.toISOString()" class="block text-3xl font-semibold leading-tight mb-6 text-app-black" :class="theme === 'red' ? 'white-text' : ''">{{
      $d(date, 'long-date')
    }}</time>

    <ul
      class="flex flex-wrap justify-between text-base text-app-black mb-4 lg:mb-6"
      :class="{
        'lg:text-white': theme === 'red',
      }"
    >
      <li v-if="eventDate" class="flex items-center basis-1/4 lg:justify-center lg:basis-1/3">
        <AtomIcon icon="app-clock" classes="w-4 mr-1 shrink-0" />
        <time :datetime="eventDate.toISOString()" class="text-xs font-semibold md:text-base shrink-0">
          <template v-if="eventEndDate">{{ $d(eventDate, 'short-time') }}<span class="px-1">–</span>{{ $d(eventEndDate, 'short-time') }}</template>
          <template v-else>{{ $d(eventDate, 'short-time') }}</template>
        </time>
      </li>
      <li v-if="location" class="flex items-center justify-center lg:basis-1/3">
        <AtomIcon icon="app-location-marker" classes="w-4 mr-1" />
        <span class="text-xs font-semibold md:text-base">{{ location }}</span>
      </li>
      <li v-if="price" class="flex items-left basis-1/4 justify-end lg:justify-center lg:basis-1/3">
        <AtomIcon icon="app-euro" classes="w-4 mr-1" />
        <span class="text-xs font-semibold md:text-base content-center">{{ $t('event-from-price', { price: formatPrice($n(price)) }) }}</span>
      </li>
    </ul>

    <MoleculeEventDetailOrderButtons
      :order-url="url"
      :stream-url="streamUrl"
      :sale-is-available="saleIsAvailable"
      :concert-state="concertState"
      :theme="theme"
      @order-click="orderClick"
    />

    <!--TODO: This needs to be styled, discuss with Valeria-->
    <p v-if="saleIsAvailableInFuture" class="mt-4 text-base font-medium">{{ $t('event-detail-available-from-datetime') }} {{ $d(saleAvailableFromDate, 'long-date-time') }}</p>

    <div class="flex justify-between">
      <MoleculeButton
        size="medium"
        fallback-component="button"
        :enable-icon-left="false"
        :shadowed="false"
        :border="[
          theme
            ? `t-cdp-border-color-v2-${theme} t-cdp-button-bg-v2-${theme} t-cdp-text-v2-${theme}`
            : 'border-app-secondary-9-green--sherpa-blue bg-app-secondary-8-green--vista-blue',
          'lg:border-solid lg:border-2',
          { 'border border-app-secondary-9-green--sherpa-blue lg:border-0': theme === 'dark-green' },
        ]"
        class="my-3"
        :class="theme === 'dark-green' ? 'thin-border' : ''"
        text-color="text-app-secondary-9-green--sherpa-blue"
        background-color="bg-app-secondary-8-green--vista-blue lg:!bg-transparent"
        @click="$emit('toggleFavorite')"
      >
        <AtomIconBookmark class="w-[18px] h-[18px] inline-block mr-2" />
        {{ isFavorite ? t_('remove-favorite') : t_('save-favorite') }}
      </MoleculeButton>

      <div v-if="saleIcon && saleTemplate === 'tix-sale-status-few-tickets'" class="c-info-data-status inline-flex items-center px-2">
        <AtomIcon :icon="saleIcon" classes="w-4 mr-2 shrink-0" :class="theme ? `t-cdp-text-last-tickets-${theme}` : 'text-app-primary-1-red--torch'" />
        <span class="text-base leading-none" :class="theme ? `t-cdp-text-last-tickets-${theme}` : 'text-app-primary-1-red--torch'">
          {{ $t(saleTemplate.toString()) }}
        </span>
      </div>
    </div>

    <div v-if="additionalEvents.length > 0">
      <h4 class="mt-6 mb-1 font-semibold">{{ $t('event-other-dates') }}</h4>
      <div class="leading-none text-sm">
        <MoleculeEventDetailPageOtherDates :additional-events="additionalEvents.slice(0, 3)" :show-more-event-dates="showMoreEventDates" />
        <template v-if="additionalEvents.length > 3">
          <SmoothReflow class="overflow-hidden transition-all duration-500 ease-in-out">
            <div :class="{ 'h-auto': showMoreEventDates, 'h-0': !showMoreEventDates }">
              <MoleculeEventDetailPageOtherDates :additional-events="additionalEvents.slice(3)" />
            </div>
          </SmoothReflow>
          <button class="underline inline-block mt-6" @click="toggleShowAll">
            {{ showMoreEventDates ? $t('event-hide-all-dates') : $t('event-see-all-dates') }}
            <AtomIconChevronDown class="ml-2 transform transition duration-200 inline-block w-4 h-4" :class="{ 'rotate-180': showMoreEventDates }" />
          </button>
        </template>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
const props = defineProps({
  url: { type: String, required: false, default: undefined },
  streamUrl: { type: String, required: false, default: undefined },
  eventDate: { type: Date, required: false, default: undefined },
  eventEndDate: { type: Date, required: false, default: undefined },
  location: { type: String, required: false, default: undefined },
  price: { type: Number, required: false, default: undefined },
  theme: { type: String, required: false, default: undefined },
  date: { type: Date, required: false, default: undefined },
  isFavorite: { type: Boolean, required: true },
  additionalEvents: { type: Array as PropType<any[]>, required: false, default: () => [] },

  // Sale information from `setupTixSale`
  tixSale: { type: Object as PropType<Record<string, any>>, required: false, default: () => ({}) },
  // concert status from "IConcert"
  concertStatus: { type: Object as PropType<Record<string, any>>, required: false, default: () => ({}) },
  concertState: { type: Object as PropType<Record<string, any>>, required: false, default: () => ({}) },
})

const emit = defineEmits(['orderClick', 'toggleFavorite'])

const { t_ } = setupTranslationPrefixer('event-detail')
const showMoreEventDates = ref(false)
const { saleAvailableFromDate, saleIcon, saleIsAvailable, saleIsAvailableInFuture, saleTemplate } = props.tixSale
const gtmCdpAction = setupGtmCdpAction()

const toggleShowAll = () => {
  showMoreEventDates.value = !showMoreEventDates.value
  if (showMoreEventDates.value) {
    gtmCdpAction('CV07 - See all dates clicked')
  }
}

function orderClick() {
  emit('orderClick')
}

// Receives `$n(price)`, i.e. the locale-formatted string, hence the comma-to-dot step below.
function formatPrice(price: string | number) {
  const numberPrice = parseFloat(price.toString().replace(',', '.'))
  const formattedPrice = numberPrice.toFixed(2)
  return formattedPrice.replace('.', ',')
}
</script>

<style lang="scss">
// scoped scss is not suitable for this component, it won't work in this case because it needs to inherit styles from parent elements
.c-info-available-from-sticky-desktop {
  @apply hidden;

  .i-is-stuck-on-desktop & {
    @apply block;
  }
}

.c-info-data-title-v2 {
  @apply hidden;

  .i-is-stuck-on-mobile & {
    @apply block mr-3;
  }

  .i-is-stuck-on-desktop & {
    @apply hidden;
  }
}

.thin-border {
  border-width: 1px;
}

.white-text {
  @screen lg {
    color: white;
  }
}
</style>
